import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminSystemType, UpdateSystemTypeNewImageBody, UpdateSystemTypeRemoveImageBody } from "./types/systemTypes";

export const useSystemTypesApi = () => {
  const api = useApi();

  const getAdminSystemType = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/system_types`;
      return useSWRMutation<AdminSystemType, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminSystemType>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminSystemTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/system_types", query);
      return useSWR<Paginated<AdminSystemType>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminSystemType>>({ url, signal }),
      );
    },
    [api],
  );

  // const insertAdminSystemType = useCallback(
  //   async (body: InsertSystemTypeBody): Promise<{ id: string }> => {
  //     const url = `/admin/settings/patrol/system_types`;
  //     const id = await api.post<{ id: string }, InsertSystemTypeBody>(url, body);
  //     mutateCache([url]);
  //     return id;
  //   },
  //   [api]
  // );

  const updateAdminSystemType = useCallback(
    async (
      systemTypeId: string,
      body: UpdateSystemTypeRemoveImageBody | UpdateSystemTypeNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/system_types/${systemTypeId}`;
      (await api.put<UpdateSystemTypeRemoveImageBody | UpdateSystemTypeNewImageBody>(url, body),
        mutateCache(["/admin/settings/patrol/system_types"]));
    },
    [api],
  );

  // const deleteAdminSystemType = useCallback(
  //   async (systemTypeId: string): Promise<void> => {
  //     const url = `/admin/settings/patrol/system_types/${systemTypeId}`;
  //     await api.del(url)
  //       mutateCache([`/admin/settings/patrol/system_types`]);
  //   },
  //   [api]
  // );

  const generateUploadImageUrl = useCallback(
    async (systemTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/system_types/${systemTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminSystemTypeVisibility = useCallback(
    async (systemTypeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/system_types/${systemTypeId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/system_types"]);
    },
    [api],
  );

  const updateAdminSystemTypeOrder = useCallback(
    async (systemTypeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/system_types/${systemTypeId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/system_types"]);
    },
    [api],
  );

  return {
    getAdminSystemType,
    useAdminSystemTypes,
    // insertAdminSystemType,
    updateAdminSystemType,
    updateAdminSystemTypeVisibility,
    updateAdminSystemTypeOrder,
    // deleteAdminSystemType,
    generateUploadImageUrl,
  };
};
